// ConfirmModal.tsx — Double-tap-to-confirm overlay for admin write actions.
//
// Rendered as a full-viewport overlay. Cancel restores no-op state.
// Confirm calls onConfirm() which should dispatch the canister mutation.
//
// Copy: no exclamation marks. Cinzel labels. Cormorant body. Token colors only.
// Brand Bible Ch 9: authoritative; Ch 3: never casual.
//
// Accessibility: focus-traps within the modal; ESC key cancels.

import React, { useCallback, useEffect, useRef } from 'react';

interface ConfirmModalProps {
  title: string;
  body: string | React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  isDestructive?: boolean;
  isLoading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function ConfirmModal({
  title,
  body,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  isDestructive = false,
  isLoading = false,
  onConfirm,
  onCancel,
}: ConfirmModalProps): React.ReactElement {
  const cancelRef = useRef<HTMLButtonElement>(null);

  // Auto-focus cancel on mount (safer default for destructive actions).
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  // ESC key dismisses.
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') onCancel();
  }, [onCancel]);

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      onKeyDown={handleKeyDown}
    >
      <div className="modal-card">
        <h2 className="modal-title" id="modal-title">{title}</h2>
        <div className="modal-body">
          {typeof body === 'string' ? (
            <p className="modal-body-text">{body}</p>
          ) : (
            body
          )}
        </div>
        <p className="modal-audit-note">This action is recorded in the Audit Log.</p>
        <div className="modal-actions">
          <button
            ref={cancelRef}
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={isLoading}
          >
            {cancelLabel}
          </button>
          <button
            className={`btn${isDestructive ? ' btn-destructive' : ''}`}
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? 'Processing' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
